import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Search, X, Pencil, Trash2, ChevronLeft, ChevronRight,
  Tag, RefreshCw, AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { adminDiscountCodesAPI } from '../../services/api';

// Brand palette — aligned with onboarding primitives (do NOT import primitives.js).
const BRAND = {
  greenDark: '#0F6E56',
  green: '#1D9E75',
  text: '#1A1A18',
  muted: '#888780',
  surface: '#FDFCF9',
  surfaceAlt: '#F7F6F1',
  mint: '#E1F5EE',
  border: '#E8E5DD',
  error: '#E24B4A',
};

// Available plan slugs to include in discount. Can be multi-select.
const AVAILABLE_PLANS = [
  { slug: 'basic', label: 'الأساسية' },
  { slug: 'advanced', label: 'المتقدمة' },
  { slug: 'professional', label: 'الاحترافية' },
];

const formatDate = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString('ar-SA-u-ca-gregory');
  } catch {
    return String(v);
  }
};

const toInputDate = (v) => {
  if (!v) return '';
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
};

const planLabelFromSlugs = (slugs) => {
  if (!slugs || slugs === 'all' || (Array.isArray(slugs) && slugs.length === 0)) {
    return 'جميع الباقات';
  }
  const arr = Array.isArray(slugs) ? slugs : [slugs];
  return arr
    .map((s) => AVAILABLE_PLANS.find((p) => p.slug === s)?.label || s)
    .join('، ');
};

const StatusPill = ({ active }) => (
  <span
    className="inline-flex items-center gap-1.5 text-xs font-semibold"
    style={{ color: active ? BRAND.greenDark : BRAND.muted }}
  >
    <span
      className="w-1.5 h-1.5 rounded-full"
      style={{ background: active ? BRAND.green : BRAND.muted }}
    />
    {active ? 'نشط' : 'معطّل'}
  </span>
);

// ─── Modal ───────────────────────────────────────────────
const emptyForm = {
  code: '',
  type: 'percentage',
  value: '',
  maxUses: '',
  maxUsesPerTenant: 1,
  validFrom: '',
  validUntil: '',
  minInvoiceAmount: '',
  plans: 'all', // 'all' or array of slugs
  isActive: true,
  notes: '',
};

const DiscountModal = ({ initial, onClose, onSaved, onDeleted }) => {
  const isEdit = Boolean(initial?._id || initial?.id);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!initial) return;
    // Backend returns plans as { mode: 'all'|'specific', slugs: [] }. Also
    // accept legacy shapes (`includedPlans`, a bare array) during the transition.
    let plans = 'all';
    if (initial.plans && typeof initial.plans === 'object' && !Array.isArray(initial.plans)) {
      plans = initial.plans.mode === 'specific' && Array.isArray(initial.plans.slugs)
        ? initial.plans.slugs
        : 'all';
    } else {
      const includes = initial.includedPlans ?? initial.plans;
      plans = !includes || includes === 'all' || (Array.isArray(includes) && includes.length === 0)
        ? 'all'
        : (Array.isArray(includes) ? includes : [includes]);
    }
    setForm({
      code: initial.code || '',
      type: initial.type || 'percentage',
      value: initial.value ?? '',
      maxUses: initial.maxTotalUses ?? initial.maxUses ?? initial.limit ?? '',
      maxUsesPerTenant: initial.maxUsesPerTenant ?? 1,
      validFrom: toInputDate(initial.startsAt || initial.validFrom),
      validUntil: toInputDate(initial.expiresAt || initial.validUntil || initial.expires),
      minInvoiceAmount: initial.minOrderAmount ?? initial.minInvoiceAmount ?? '',
      plans,
      isActive: initial.isActive ?? initial.active ?? true,
      notes: initial.internalNote ?? initial.notes ?? initial.internalNotes ?? '',
    });
  }, [initial]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (confirmDelete) setConfirmDelete(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', handler);
    firstFieldRef.current?.focus();
    return () => window.removeEventListener('keydown', handler);
  }, [confirmDelete, onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generateCode = () => {
    const rnd = Math.random().toString(36).slice(2, 8).toUpperCase();
    set('code', rnd);
  };

  const togglePlan = (slug) => {
    setForm((f) => {
      const current = f.plans === 'all' ? [] : f.plans;
      const exists = current.includes(slug);
      const next = exists ? current.filter((s) => s !== slug) : [...current, slug];
      return { ...f, plans: next.length === 0 ? 'all' : next };
    });
  };

  const selectAllPlans = () => set('plans', 'all');

  const validate = () => {
    if (!form.code.trim()) return 'الكود مطلوب';
    if (form.value === '' || isNaN(Number(form.value)) || Number(form.value) <= 0) {
      return 'القيمة يجب أن تكون رقماً أكبر من صفر';
    }
    if (form.type === 'percentage' && Number(form.value) > 100) {
      return 'النسبة المئوية لا يمكن أن تتجاوز 100%';
    }
    if (!form.validFrom) return 'تاريخ بداية الصلاحية مطلوب';
    if (form.validUntil && form.validFrom && form.validUntil < form.validFrom) {
      return 'تاريخ الانتهاء يجب أن يكون بعد تاريخ البداية';
    }
    return null;
  };

  const buildPayload = () => ({
    code: form.code.trim().toUpperCase(),
    type: form.type,
    value: Number(form.value),
    maxTotalUses: form.maxUses === '' ? null : Number(form.maxUses),
    maxUsesPerTenant: form.maxUsesPerTenant === '' ? 1 : Number(form.maxUsesPerTenant),
    startsAt: form.validFrom || null,
    expiresAt: form.validUntil || null,
    minOrderAmount: form.minInvoiceAmount === '' ? 0 : Number(form.minInvoiceAmount),
    plans: form.plans === 'all'
      ? { mode: 'all', slugs: [] }
      : { mode: 'specific', slugs: Array.isArray(form.plans) ? form.plans : [] },
    isActive: Boolean(form.isActive),
    internalNote: form.notes || '',
  });

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await adminDiscountCodesAPI.update(initial._id || initial.id, payload);
        toast.success('تم تحديث الكود');
      } else {
        await adminDiscountCodesAPI.create(payload);
        toast.success('تم إنشاء الكود');
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'فشل في حفظ الكود');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminDiscountCodesAPI.delete(initial._id || initial.id);
      toast.success('تم حذف الكود');
      onDeleted?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'فشل في حذف الكود');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(24,20,16,0.5)' }}
        onClick={onClose}
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'تعديل كود خصم' : 'كود خصم جديد'}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl w-full overflow-hidden flex flex-col"
          style={{ maxWidth: 600, maxHeight: '92vh' }}
        >
          <div
            className="px-6 py-4 flex items-center justify-between"
            style={{ borderBottom: `1px solid ${BRAND.border}` }}
          >
            <h2 className="text-lg font-bold" style={{ color: BRAND.text }}>
              {isEdit ? 'تعديل كود' : 'كود خصم جديد'}
            </h2>
            <button
              onClick={onClose}
              aria-label="إغلاق"
              className="w-8 h-8 flex items-center justify-center rounded-lg border bg-white hover:bg-gray-50"
              style={{ borderColor: BRAND.border }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
            {/* Code */}
            <div>
              <label className="block text-[13px] font-medium mb-2" style={{ color: BRAND.text }}>
                الكود <span style={{ color: BRAND.error }}>*</span>
              </label>
              <div className="relative">
                <input
                  ref={firstFieldRef}
                  type="text"
                  dir="ltr"
                  value={form.code}
                  onChange={(e) => set('code', e.target.value.toUpperCase().replace(/\s/g, ''))}
                  placeholder="LAUNCH20"
                  className="w-full py-3 text-sm rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                  style={{
                    border: `1px solid ${BRAND.border}`,
                    paddingInlineStart: 14,
                    paddingInlineEnd: 96,
                    fontFamily: 'ui-monospace, monospace',
                  }}
                />
                <button
                  type="button"
                  onClick={generateCode}
                  className="absolute top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold"
                  style={{
                    insetInlineEnd: 8,
                    background: BRAND.mint,
                    color: BRAND.greenDark,
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <RefreshCw className="w-3 h-3" />
                  توليد
                </button>
              </div>
            </div>

            {/* Type */}
            <div>
              <label className="block text-[13px] font-medium mb-2" style={{ color: BRAND.text }}>
                نوع الخصم <span style={{ color: BRAND.error }}>*</span>
              </label>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { k: 'percentage', l: 'نسبة مئوية' },
                  { k: 'fixed_amount', l: 'مبلغ ثابت' },
                ].map((o) => {
                  const selected = form.type === o.k;
                  return (
                    <label
                      key={o.k}
                      className="flex items-center gap-2.5 p-3.5 rounded-xl cursor-pointer transition-colors"
                      style={{
                        border: `1.5px solid ${selected ? BRAND.green : BRAND.border}`,
                        background: selected ? BRAND.mint : '#fff',
                      }}
                    >
                      <input
                        type="radio"
                        checked={selected}
                        onChange={() => set('type', o.k)}
                        style={{ accentColor: BRAND.green }}
                      />
                      <span className="text-sm font-medium">{o.l}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Value */}
            <div>
              <label className="block text-[13px] font-medium mb-2" style={{ color: BRAND.text }}>
                القيمة <span style={{ color: BRAND.error }}>*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.value}
                  onChange={(e) => set('value', e.target.value)}
                  className="w-full py-3 text-sm rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                  style={{
                    border: `1px solid ${BRAND.border}`,
                    paddingInlineStart: 14,
                    paddingInlineEnd: 50,
                  }}
                />
                <span
                  className="absolute top-1/2 -translate-y-1/2 text-sm font-medium"
                  style={{ insetInlineEnd: 14, color: BRAND.muted }}
                >
                  {form.type === 'percentage' ? '%' : 'ر.س'}
                </span>
              </div>
            </div>

            {/* Limits */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-medium mb-2" style={{ color: BRAND.text }}>
                  الحد الأقصى للاستخدامات
                  <span className="text-[11px] font-normal mr-1" style={{ color: BRAND.muted }}>
                    (اختياري — فارغ = بلا حدود)
                  </span>
                </label>
                <input
                  type="number"
                  min={0}
                  value={form.maxUses}
                  onChange={(e) => set('maxUses', e.target.value)}
                  placeholder="100"
                  className="w-full px-3.5 py-3 text-sm rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                  style={{ border: `1px solid ${BRAND.border}` }}
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium mb-2" style={{ color: BRAND.text }}>
                  الحد الأقصى لكل مختبر
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.maxUsesPerTenant}
                  onChange={(e) => set('maxUsesPerTenant', e.target.value)}
                  className="w-full px-3.5 py-3 text-sm rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                  style={{ border: `1px solid ${BRAND.border}` }}
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-medium mb-2" style={{ color: BRAND.text }}>
                  تاريخ بداية الصلاحية <span style={{ color: BRAND.error }}>*</span>
                </label>
                <input
                  type="date"
                  dir="ltr"
                  value={form.validFrom}
                  onChange={(e) => set('validFrom', e.target.value)}
                  className="w-full px-3.5 py-3 text-sm rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                  style={{ border: `1px solid ${BRAND.border}` }}
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium mb-2" style={{ color: BRAND.text }}>
                  تاريخ الانتهاء
                  <span className="text-[11px] font-normal mr-1" style={{ color: BRAND.muted }}>
                    (اختياري)
                  </span>
                </label>
                <input
                  type="date"
                  dir="ltr"
                  value={form.validUntil}
                  onChange={(e) => set('validUntil', e.target.value)}
                  className="w-full px-3.5 py-3 text-sm rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                  style={{ border: `1px solid ${BRAND.border}` }}
                />
              </div>
            </div>

            {/* Min invoice */}
            <div>
              <label className="block text-[13px] font-medium mb-2" style={{ color: BRAND.text }}>
                الحد الأدنى للفاتورة
                <span className="text-[11px] font-normal mr-1" style={{ color: BRAND.muted }}>
                  (اختياري)
                </span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  value={form.minInvoiceAmount}
                  onChange={(e) => set('minInvoiceAmount', e.target.value)}
                  placeholder="0"
                  className="w-full py-3 text-sm rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                  style={{
                    border: `1px solid ${BRAND.border}`,
                    paddingInlineStart: 14,
                    paddingInlineEnd: 50,
                  }}
                />
                <span
                  className="absolute top-1/2 -translate-y-1/2 text-sm font-medium"
                  style={{ insetInlineEnd: 14, color: BRAND.muted }}
                >
                  ر.س
                </span>
              </div>
            </div>

            {/* Included plans */}
            <div>
              <label className="block text-[13px] font-medium mb-2" style={{ color: BRAND.text }}>
                الباقات المشمولة <span style={{ color: BRAND.error }}>*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={selectAllPlans}
                  className="px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
                  style={{
                    border: `1.5px solid ${form.plans === 'all' ? BRAND.green : BRAND.border}`,
                    background: form.plans === 'all' ? BRAND.mint : '#fff',
                    color: form.plans === 'all' ? BRAND.greenDark : BRAND.text,
                  }}
                >
                  جميع الباقات
                </button>
                {AVAILABLE_PLANS.map((p) => {
                  const selected = form.plans !== 'all' && Array.isArray(form.plans) && form.plans.includes(p.slug);
                  return (
                    <button
                      key={p.slug}
                      type="button"
                      onClick={() => togglePlan(p.slug)}
                      className="px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
                      style={{
                        border: `1.5px solid ${selected ? BRAND.green : BRAND.border}`,
                        background: selected ? BRAND.mint : '#fff',
                        color: selected ? BRAND.greenDark : BRAND.text,
                      }}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active toggle */}
            <label
              className="flex items-center gap-3 p-3.5 rounded-xl cursor-pointer"
              style={{ background: BRAND.surfaceAlt }}
            >
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => set('isActive', e.target.checked)}
                className="w-4 h-4"
                style={{ accentColor: BRAND.green }}
              />
              <span className="text-sm font-medium">الكود نشط</span>
            </label>

            {/* Notes */}
            <div>
              <label className="block text-[13px] font-medium mb-2" style={{ color: BRAND.text }}>
                ملاحظات داخلية
                <span className="text-[11px] font-normal mr-1" style={{ color: BRAND.muted }}>
                  (اختياري — لا يظهر للمختبرات)
                </span>
              </label>
              <textarea
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                rows={3}
                className="w-full px-3.5 py-3 text-sm rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                style={{ border: `1px solid ${BRAND.border}`, fontFamily: 'inherit' }}
              />
            </div>

            {/* Delete confirmation inline */}
            {confirmDelete && (
              <div
                className="rounded-xl p-4"
                style={{
                  background: 'rgba(226,75,74,0.06)',
                  border: `1px solid rgba(226,75,74,0.25)`,
                }}
              >
                <div className="flex items-start gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: BRAND.error }} />
                  <div>
                    <div className="text-sm font-semibold" style={{ color: BRAND.error }}>
                      هل أنت متأكد من حذف هذا الكود؟
                    </div>
                    <div className="text-xs mt-1" style={{ color: BRAND.error, opacity: 0.85 }}>
                      سيتم تعطيل الكود بشكل نهائي. لا يمكن التراجع.
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                    className="flex-1 py-2 rounded-lg text-sm font-semibold border bg-white disabled:opacity-50"
                    style={{ borderColor: BRAND.border, color: BRAND.text }}
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex-1 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                    style={{ background: BRAND.error }}
                  >
                    {deleting ? 'جارٍ الحذف...' : 'تأكيد الحذف'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div
            className="px-6 py-4 flex items-center justify-between gap-2"
            style={{ borderTop: `1px solid ${BRAND.border}` }}
          >
            {isEdit ? (
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={saving || confirmDelete}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                style={{ color: BRAND.error, background: 'transparent' }}
              >
                <Trash2 className="w-4 h-4" />
                حذف
              </button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 rounded-xl text-sm font-semibold border bg-white hover:bg-gray-50 disabled:opacity-50"
                style={{ borderColor: BRAND.border, color: BRAND.text }}
              >
                إلغاء
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 rounded-xl text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                style={{ background: BRAND.green }}
              >
                {saving ? 'جارٍ الحفظ...' : isEdit ? 'حفظ التعديلات' : 'إنشاء الكود'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── Main Page ───────────────────────────────────────────
const DiscountCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editing, setEditing] = useState(null); // null | {} for new | existing obj for edit
  const [togglingId, setTogglingId] = useState(null);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      const res = await adminDiscountCodesAPI.list(params);
      const result = res.data?.data || res.data || {};
      const items = Array.isArray(result) ? result : (result.items || result.codes || []);
      setCodes(items);
      setTotalPages(result.totalPages || result.pages || 1);
    } catch (e) {
      toast.error('فشل في تحميل أكواد الخصم');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, typeFilter, page]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);
  useEffect(() => { setPage(1); }, [search, statusFilter, typeFilter]);

  const handleToggle = async (code) => {
    const id = code._id || code.id;
    setTogglingId(id);
    try {
      await adminDiscountCodesAPI.toggle(id);
      toast.success('تم تحديث حالة الكود');
      fetchCodes();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'فشل في تحديث حالة الكود');
    } finally {
      setTogglingId(null);
    }
  };

  const openNew = () => setEditing({});
  const openEdit = (code) => setEditing(code);

  return (
    <div dir="rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: BRAND.text }}>أكواد الخصم</h1>
          <p className="text-sm mt-1" style={{ color: BRAND.muted }}>
            أنشئ وأدر أكواد الخصم للمختبرات.
          </p>
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-white hover:opacity-90"
          style={{ background: BRAND.green }}
        >
          <Plus className="w-4 h-4" />
          إنشاء كود جديد
        </button>
      </div>

      {/* Filters */}
      <div
        className="bg-white rounded-2xl p-4 mb-4"
        style={{ border: `1px solid ${BRAND.border}` }}
      >
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1 min-w-0">
            <Search
              className="absolute w-4 h-4 top-1/2 -translate-y-1/2"
              style={{ insetInlineStart: 12, color: BRAND.muted }}
            />
            <input
              type="text"
              placeholder="بحث بالكود..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full py-2.5 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              style={{
                border: `1px solid ${BRAND.border}`,
                paddingInlineStart: 36,
                paddingInlineEnd: 14,
              }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3.5 py-2.5 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
            style={{ border: `1px solid ${BRAND.border}` }}
          >
            <option value="all">كل الحالات</option>
            <option value="active">نشط</option>
            <option value="inactive">معطّل</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3.5 py-2.5 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
            style={{ border: `1px solid ${BRAND.border}` }}
          >
            <option value="all">كل الأنواع</option>
            <option value="percentage">نسبة</option>
            <option value="fixed_amount">مبلغ ثابت</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div
        className="bg-white rounded-2xl overflow-hidden"
        style={{ border: `1px solid ${BRAND.border}` }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr style={{ background: BRAND.surfaceAlt, borderBottom: `1px solid ${BRAND.border}` }}>
                {['الكود', 'النوع', 'القيمة', 'يشمل', 'الاستخدامات', 'تنتهي', 'الحالة', ''].map((h, i) => (
                  <th
                    key={i}
                    className="text-start px-4 py-3 text-xs font-semibold"
                    style={{ color: BRAND.muted, letterSpacing: 0.3 }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse" style={{ borderBottom: `1px solid ${BRAND.surfaceAlt}` }}>
                    {[...Array(8)].map((_, j) => (
                      <td key={j} className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded" style={{ width: j === 0 ? 100 : 70 }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : codes.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-14" style={{ color: BRAND.muted }}>
                    <Tag className="w-10 h-10 mx-auto mb-2" style={{ color: '#D5D3CC' }} />
                    لا توجد أكواد خصم مطابقة
                  </td>
                </tr>
              ) : (
                codes.map((c) => {
                  const id = c._id || c.id;
                  // Backend stores redemption count under `redemptions.totalUses`.
                  const used = c.redemptions?.totalUses ?? c.usedCount ?? c.used ?? 0;
                  const limit = c.maxTotalUses ?? c.maxUses ?? c.limit;
                  const expires = c.expiresAt || c.validUntil || c.expires;
                  const isActive = c.isActive ?? c.active ?? false;
                  // Backend shape: { mode, slugs }. Legacy shapes (bare array /
                  // 'all' string / includedPlans) are still accepted for safety.
                  const plansField = c.plans;
                  const plans = plansField && typeof plansField === 'object' && !Array.isArray(plansField)
                    ? (plansField.mode === 'specific' ? plansField.slugs : 'all')
                    : (c.includedPlans ?? plansField);
                  return (
                    <motion.tr
                      key={id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="transition-colors hover:bg-[#F7F6F1]"
                      style={{ borderBottom: `1px solid ${BRAND.surfaceAlt}` }}
                    >
                      <td className="px-4 py-3.5">
                        <code
                          className="font-mono font-bold text-[13px]"
                          style={{
                            background: BRAND.mint,
                            color: BRAND.greenDark,
                            padding: '3px 10px',
                            borderRadius: 6,
                            letterSpacing: 0.3,
                          }}
                        >
                          {c.code}
                        </code>
                      </td>
                      <td className="px-4 py-3.5" style={{ color: BRAND.muted }}>
                        {c.type === 'percentage' ? 'نسبة' : 'مبلغ ثابت'}
                      </td>
                      <td
                        className="px-4 py-3.5 font-semibold"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {c.type === 'percentage' ? `${c.value}%` : `${Number(c.value).toLocaleString()} ر.س`}
                      </td>
                      <td className="px-4 py-3.5" style={{ color: BRAND.muted }}>
                        {planLabelFromSlugs(plans)}
                      </td>
                      <td
                        className="px-4 py-3.5"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {used}{limit ? `/${limit}` : '/∞'}
                      </td>
                      <td
                        className="px-4 py-3.5"
                        style={{ color: BRAND.muted, fontVariantNumeric: 'tabular-nums' }}
                      >
                        {expires ? formatDate(expires) : 'بلا انتهاء'}
                      </td>
                      <td className="px-4 py-3.5">
                        <button
                          onClick={() => handleToggle(c)}
                          disabled={togglingId === id}
                          className="transition-opacity disabled:opacity-50"
                          title={isActive ? 'اضغط للتعطيل' : 'اضغط للتفعيل'}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                        >
                          <StatusPill active={isActive} />
                        </button>
                      </td>
                      <td className="px-4 py-3.5 text-end">
                        <button
                          onClick={() => openEdit(c)}
                          className="inline-flex items-center gap-1 text-sm font-medium"
                          style={{ color: BRAND.greenDark, background: 'transparent', border: 'none', cursor: 'pointer' }}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          تعديل
                        </button>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderTop: `1px solid ${BRAND.border}` }}
          >
            <p className="text-sm" style={{ color: BRAND.muted }}>
              صفحة {page} من {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
                aria-label="السابق"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
                aria-label="التالي"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {editing && (
        <DiscountModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={fetchCodes}
          onDeleted={fetchCodes}
        />
      )}
    </div>
  );
};

export default DiscountCodes;
